import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LocalDate } from "@js-joda/core";

import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { Modal } from "components/designV2/modal/modal";
import { ColorV2 } from "config/colors";
import type {
  TimeReportsV2Filter,
  TimeReportsV2Group,
} from "services/api/company/timeReports.service";
import { ReportAbsenceModalV2 } from "web-apps/company/components/common/modals/reportAbsenceModalV2.component";
import { resolveTimeReportsDisplaySettings } from "web-apps/company/components/timeReportsV2/timeReports.settings.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type { TimeReportsV2DisplayOption } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import type { TimeReportsGroupByOption } from "web-apps/company/components/timeReportsV2/components/toolbar/timeReportsToolbar.component";
import { ActionCommentModal } from "./components/actionCommentModal.component";
import { TimeReportDetailsPanel } from "./components/detailsPanel/timeReportDetailsPanel.component";
import { PersonRowsList } from "./components/personRowsList.component";
import { PersonBulkActionsBar } from "./components/personBulkActionsBar.component";
import { usePersonBulkActions } from "./hooks/usePersonBulkActions";
import { usePersonSelection } from "./hooks/usePersonSelection";
import type { PersonDetailSelection } from "./models/personView.types";
import { useTimeReportsPersonView } from "./hooks/useTimeReportsPersonView";
import type { InlineTimeReportEditActivity } from "./hooks/useInlineTimeReportEdit";
import { resolveBulkActionModalCopy } from "./models/personView.bulkActionModal.logic";
import { formatSignedTotalMinutes } from "./models/personView.actions";
import { exportPersonRowToCsv } from "./personView.csvExport";
import {
  ContentWrap,
  DimmedContent,
  FailedItem,
  FailedItemReason,
  FailedItemsList,
  FailedItemsTitle,
  FailedItemTitle,
  FeedbackBody,
  FeedbackMessage,
  LoaderWrap,
  ListSelectAllRow,
  LoadingOverlay,
  LoadMoreSentinel,
  LoadMoreWrap,
  Message,
  RowList,
  Spinner,
  ViewContainer,
} from "./personView.styled";
import { resolveTimeReportAccess } from "./models/timeReportAccess.logic";

interface PersonViewProps {
  readonly dateRange: { fromDate: LocalDate; toDate: LocalDate } | null;
  readonly displayOption: TimeReportsV2DisplayOption;
  readonly filters?: TimeReportsV2Filter[];
  readonly groupByOption: TimeReportsGroupByOption;
  readonly isAllRowsExpanded: boolean;
  readonly isPreferencesHydrated: boolean;
  readonly monthDate: LocalDate;
  readonly queryScopeKey: string;
  readonly workGroupIds: number[];
  readonly workerIds: number[];
}

const APPROVE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

const DENY_SECONDARY_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  borderColor: ColorV2.NegativeRed,
  textColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: "rgba(211, 78, 78, 0.12)",
  activeBackgroundColor: "rgba(211, 78, 78, 0.2)",
};

const DENY_MODAL_PRIMARY_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.DeclinedRed,
  borderColor: "transparent",
  textColor: ColorV2.White,
  iconColor: ColorV2.White,
  hoverBackgroundColor: ColorV2.NegativeRed,
  activeBackgroundColor: ColorV2.NegativeRed,
};

export const PersonView = (props: PersonViewProps): JSX.Element => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const { isReadOnly, restrictedActions } = resolveTimeReportAccess({
    companyId: companyState.company?.id,
    isLoading: companyState.subscription.isLoading,
    subscription: companyState.subscription.info,
  });

  const viewState = useTimeReportsPersonView({
    monthDate: props.monthDate,
    groupByOption: props.groupByOption,
    workGroupIds: props.workGroupIds,
    workerIds: props.workerIds,
    filters: props.filters,
    dateRange: props.dateRange,
    isPreferencesHydrated: props.isPreferencesHydrated,
  });

  const [pendingInlineEdits, setPendingInlineEdits] = useState<
    ReadonlySet<string>
  >(new Set());
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const inlineSaveScope = `${companyState.company?.id}|${props.queryScopeKey}|`;
  const handleInlineEditPendingChange = useCallback(
    ({
      hasPendingChanges,
      timeReportId,
    }: InlineTimeReportEditActivity): void => {
      if (!isMountedRef.current) return;
      const key = `${inlineSaveScope}${timeReportId}`;
      setPendingInlineEdits((current) => {
        if (current.has(key) === hasPendingChanges) return current;
        const next = new Set(current);
        if (hasPendingChanges) next.add(key);
        else next.delete(key);
        return next;
      });
    },
    [inlineSaveScope],
  );
  const hasPendingInlineChanges =
    viewState.isInlineRefreshing ||
    Array.from(pendingInlineEdits).some((key) =>
      key.startsWith(inlineSaveScope),
    );
  const isActionBlocked =
    isReadOnly || hasPendingInlineChanges || viewState.error !== null;

  const selectionState = usePersonSelection({
    rows: viewState.rows,
    queryScopeKey: props.queryScopeKey,
  });
  const setOpenedDetail = selectionState.setOpenedDetail;
  const handleOpenDetail = useCallback(
    (selection: PersonDetailSelection): void => {
      if (!hasPendingInlineChanges) setOpenedDetail(selection);
    },
    [hasPendingInlineChanges, setOpenedDetail],
  );

  const bulkActions = usePersonBulkActions({
    companyId: companyState.company?.id,
    selectedDetails: selectionState.selectedDetails,
    openedDetail: selectionState.openedDetail,
    selectedDetailIdSet: selectionState.selectedDetailIdSet,
    clearSelection: selectionState.clearSelection,
    retainSelectionByIds: selectionState.retainSelectionByIds,
    setOpenedDetail: selectionState.setOpenedDetail,
    reload: viewState.reload,
  });

  const displaySettings = useMemo(
    () => resolveTimeReportsDisplaySettings(companyState.companySettings),
    [companyState.companySettings],
  );

  const bulkActionModalCopy = useMemo(
    () =>
      resolveBulkActionModalCopy({
        action: bulkActions.bulkCommentAction,
        translate: t,
        approveButtonStyles: APPROVE_BUTTON_STYLES,
        denyButtonStyles: DENY_MODAL_PRIMARY_BUTTON_STYLES,
      }),
    [bulkActions.bulkCommentAction, t],
  );

  const showActionError = bulkActions.showActionError;

  const handleDownloadRowCsv = useCallback(
    (row: TimeReportsV2Group): void => {
      try {
        exportPersonRowToCsv(row, props.monthDate, t);
      } catch {
        showActionError();
      }
    },
    [props.monthDate, showActionError, t],
  );

  const hasRows = viewState.rows.length > 0;
  const isOverlayLoading = viewState.isLoading && hasRows;
  const detailsPanel =
    selectionState.openedDetail?.item.id != null ? (
      <TimeReportDetailsPanel
        timeReportId={selectionState.openedDetail.item.id}
        readOnly={isReadOnly}
        restrictedActions={restrictedActions}
        onClose={() => selectionState.setOpenedDetail(null)}
        onActionCompleted={viewState.reload}
      />
    ) : null;

  return (
    <ViewContainer>
      {viewState.error && !hasRows ? (
        <Message>{viewState.error}</Message>
      ) : viewState.isLoading && !hasRows ? (
        <LoaderWrap>
          <Spinner />
        </LoaderWrap>
      ) : !viewState.isLoading && !hasRows ? (
        <Message>{t("TimeReports.NoRows")}</Message>
      ) : (
        <>
          {viewState.error && (
            <Message role="alert">
              {viewState.error}
              <SecondaryButton
                label={t("TimeReports.Retry")}
                onClick={viewState.refreshAfterInlineEdit}
              />
            </Message>
          )}
          <ContentWrap>
            {isOverlayLoading && (
              <LoadingOverlay>
                <Spinner />
              </LoadingOverlay>
            )}
            <DimmedContent $dimmed={isOverlayLoading}>
              <RowList $isDetailed={props.displayOption === "detailed"}>
                <ListSelectAllRow>
                  <Checkbox
                    label={t("CompanyTimeReport.SelectAll")}
                    checked={
                      selectionState.isAllRowsSelected
                        ? "checked"
                        : selectionState.isPartiallyRowsSelected
                          ? "indeterminate"
                          : "unchecked"
                    }
                    onChange={selectionState.toggleAllRowsSelection}
                  />
                </ListSelectAllRow>
                <PersonRowsList
                  rows={viewState.rows}
                  groupByOption={props.groupByOption}
                  displayOption={props.displayOption}
                  companyId={companyState.company?.id}
                  isReadOnly={isReadOnly || bulkActions.isBulkActionLoading}
                  onInlineEditSaved={viewState.refreshAfterInlineEdit}
                  onInlineEditPendingChange={handleInlineEditPendingChange}
                  isDetailOpeningDisabled={hasPendingInlineChanges}
                  queryScopeKey={props.queryScopeKey}
                  isAllRowsExpanded={props.isAllRowsExpanded}
                  deviationToleranceMinutes={
                    displaySettings.deviationToleranceMinutes
                  }
                  includeFlexTimeDeviations={
                    displaySettings.includeFlexTimeDeviations
                  }
                  selectedDetailIdSet={selectionState.selectedDetailIdSet}
                  onDownloadRowCsv={handleDownloadRowCsv}
                  onSelectionChange={selectionState.handleSelectionChange}
                  onDetailClick={handleOpenDetail}
                  translate={t}
                />
                {viewState.isLoadingMore && (
                  <LoadMoreWrap>
                    <Spinner />
                  </LoadMoreWrap>
                )}
                <LoadMoreSentinel ref={viewState.loadMoreRef} />
              </RowList>
            </DimmedContent>
          </ContentWrap>
          <PersonBulkActionsBar
            selectedCount={selectionState.selectedCount}
            totalTimeLabel={`${t("TimeReports.TotalTime")} ${formatSignedTotalMinutes(
              selectionState.totalSelectedMinutes,
            )}`}
            availableBulkActions={selectionState.availableBulkActions}
            readOnly={isReadOnly}
            restrictedActions={restrictedActions}
            canRunActions={
              bulkActions.canRunActions &&
              !hasPendingInlineChanges &&
              viewState.error === null
            }
            isBulkActionLoading={bulkActions.isBulkActionLoading}
            denyButtonStyles={DENY_SECONDARY_BUTTON_STYLES}
            approveButtonStyles={APPROVE_BUTTON_STYLES}
            onRunBulkAction={(action) => {
              if (isActionBlocked || restrictedActions.includes(action)) return;
              void bulkActions.runBulkAction(action);
            }}
            translate={t}
          />
        </>
      )}
      {detailsPanel}
      <ReportAbsenceModalV2
        isOpen={
          bulkActions.isAbsenceModalOpen &&
          !isReadOnly &&
          !restrictedActions.includes("absence")
        }
        onClose={bulkActions.closeAbsenceModal}
        timeReportIds={bulkActions.absenceTargetIds}
        onReportAbsence={bulkActions.handleAbsenceReported}
      />
      <Modal
        label={t("CompanyTimeReport.FutureApprovalWarningTitle")}
        isOpen={bulkActions.futureApprovalCount > 0}
        onClose={bulkActions.closeFutureApprovalModal}
        size="compact"
        centerText
        showCloseButton={false}
        footer={
          <>
            <SecondaryButton
              label={t("CompanyTimeReport.Cancel")}
              onClick={bulkActions.closeFutureApprovalModal}
            />
            <PrimaryButton
              label={t("CompanyTimeReport.FutureApprovalWarningConfirm")}
              styleOverrides={APPROVE_BUTTON_STYLES}
              onClick={() => {
                if (isActionBlocked || restrictedActions.includes("approve"))
                  return;
                bulkActions.confirmFutureApproval();
              }}
              disabled={isActionBlocked}
            />
          </>
        }
      >
        <Message>
          {t("CompanyTimeReport.FutureApprovalWarningBody", {
            count: bulkActions.futureApprovalCount,
          })}
        </Message>
      </Modal>
      {bulkActionModalCopy && bulkActions.bulkCommentAction && (
        <ActionCommentModal
          isOpen
          title={bulkActionModalCopy.title}
          confirmLabel={bulkActionModalCopy.confirmLabel}
          confirmStyleOverrides={bulkActionModalCopy.confirmStyleOverrides}
          isSubmitting={bulkActions.isBulkActionLoading}
          onClose={bulkActions.closeBulkCommentModal}
          onConfirm={async (comment) => {
            const action = bulkActions.bulkCommentAction;
            if (
              !action ||
              isActionBlocked ||
              restrictedActions.includes(action)
            )
              return false;
            return bulkActions.runCommentableBulkAction(action, comment);
          }}
        />
      )}
      <Modal
        label={
          bulkActions.feedbackState?.type === "success" ||
          bulkActions.feedbackState?.type === "partial"
            ? t("Alerts.Updated")
            : t("Alerts.OhNo")
        }
        size="compact"
        centerText
        showCloseButton={false}
        isOpen={bulkActions.feedbackState !== null}
        onClose={bulkActions.clearFeedback}
        footer={
          <PrimaryButton
            label={t("DesignComponents.Close")}
            onClick={bulkActions.clearFeedback}
          />
        }
      >
        <FeedbackBody>
          <FeedbackMessage>
            {bulkActions.feedbackState?.type === "success" &&
              t("CompanyTimeReport.TimeReportHasBeenUpdated")}
            {bulkActions.feedbackState?.type === "partial" &&
              `${t("CompanyTimeReport.TimeReportHasBeenUpdated")} (${bulkActions.feedbackState.succeededCount}/${bulkActions.feedbackState.succeededCount + bulkActions.feedbackState.failedCount})`}
            {bulkActions.feedbackState?.type === "error" &&
              t("CompanyTimeReport.UnexpectedError")}
          </FeedbackMessage>
          {(bulkActions.feedbackState?.type === "partial" ||
            bulkActions.feedbackState?.type === "error") &&
            bulkActions.feedbackState.failedItems.length > 0 && (
              <>
                <FailedItemsTitle>
                  {t("CompanyTimeReport.FailedBulkUpdatesTitle")}
                </FailedItemsTitle>
                <FailedItemsList>
                  {bulkActions.feedbackState.failedItems.map((failedItem) => (
                    <FailedItem key={failedItem.timeReportId}>
                      <FailedItemTitle>
                        {failedItem.workerName.trim().length > 0
                          ? failedItem.workerName
                          : t("CompanyTimeReport.TimeReport")}{" "}
                        {failedItem.dateLabel.trim().length > 0
                          ? `• ${failedItem.dateLabel}`
                          : `• #${failedItem.timeReportId}`}
                      </FailedItemTitle>
                      <FailedItemReason>
                        {t(failedItem.reasonKey)}
                      </FailedItemReason>
                    </FailedItem>
                  ))}
                </FailedItemsList>
              </>
            )}
        </FeedbackBody>
      </Modal>
    </ViewContainer>
  );
};
